/* global G */
import { curry, deleteKey, setKey } from 'lib/util'
import { edit } from 'app/_shared/events/message'
import { apply as applyAttachments, get as getAttachments } from 'app/_shared/events/attachment'
import updateAttachments from 'app/_shared/events/message/updateAttachments'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { set } from 'lib/sequence/component/state/value'
import { findGroup } from 'lib/util/attachment'

/**
 * Edit Status Message Event Handler
 *
 * Updates the message currently held in `module[G.STATE].currentMessage` (i.e. the
 * message the user chose to edit) instead of creating a new one.
 *
 * @param {Gaia.AppModule.Spec} module    the current module composition object
 * @param {Gaia.Component.Spec} component the message field component
 * @param {Gaia.PlatformEvent} event      the send event that triggered this handler
 * @returns {Promise<void>}
 */
const editStatusMessage = async (module, component, event) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  const { currentMessage: message = {} } = module[G.STATE]
  const key = message?.key
  const { text } = event?.detail || {}

  const attachments = await getAttachments(module, component, event)
  const attachmentsCount = attachments.length

  const callback = async (item) => {
    const { value } = item

    // Let the attachment adapter know which message freshly added attachments belong to.
    setKey(key, G.REF, component[G.STATE])

    // The attachment adapter uploads local attachments (whose key still equals their name)
    // when we apply. We know the whole process is finished once these are done.
    const localAttachments = attachments.filter(
      attachment => attachment?.value?.name && (attachment.key === attachment.value.name),
    )

    applyAttachments(module, component, event)
    await updateAttachments(module, component, localAttachments)

    // Removing the loader again after we are done.
    eventBus.dispatch(eventBus.type(G.LOAD, G.DONE))

    value.attachments = attachments

    eventBus.dispatch(eventBus.type(G.MESSAGE, G.CHANGE), {
      [G.DATA]: {
        ...item,
        value: {
          ...item.value,
          attachments,
        },
      },
    })

    // clearing attachment group
    eventBus.dispatch(
      eventBus.type(G.ATTACHMENT, G.CACHE, findGroup(module, null, 'messageGroup')),
      { [G.DATA]: { [G.DATA]: [] } },
    )

    // Leaving edit-mode and clearing the drafted text.
    const { messageField } = sequenceComponentFindProxy(module[G.STATE][G.ACTION][G.COMPONENT])
    setKey(false, 'edit', module[G.STATE])
    setKey(false, 'edit', messageField[G.STATE])

    deleteKey('currentMessage', module[G.STATE])
    deleteKey('text', module[G.STATE])

    await module[G.ADAPTER][G.UI].update(module)

    return true
  }

  const editEvent = {
    ...event,
    detail: {
      data: { key, text },
      callback,
      ...attachmentsCount && { hasAttachments: attachmentsCount },
    },
  }

  await edit(module, component, editEvent)
}

export default curry(editStatusMessage)
